// src/components/dashboard/ExtractedResultsView.tsx
import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, ArrowRight, Loader } from 'lucide-react';
import { HealthMetric, PatientInfo, apiService } from '../../services/api';
import { logger } from '../../utils/logger';

interface ExtractedResultsViewProps {
  data: {
    filename: string;
    file_type: string;
    patient_info: PatientInfo;
    health_metrics: HealthMetric[];
    metrics_count: number;
  };
  jobId: string | null;
  onNavigateToDashboard: () => void;
}

export const ExtractedResultsView: React.FC<ExtractedResultsViewProps> = ({
  data,
  jobId,
  onNavigateToDashboard
}) => {
  const [isDataReady, setIsDataReady] = useState(false);
  const [isPolling, setIsPolling] = useState(true);
  const [pollingStage, setPollingStage] = useState('Saving to database...');
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    logger.log('🔄 [ExtractedResultsView] Polling started for:', {
      filename: data.filename,
      metricsCount: data.metrics_count,
      jobId: jobId || 'NO_JOB_ID'
    });

    let pollInterval: NodeJS.Timeout;
    let countdownInterval: NodeJS.Timeout;
    let elapsedSeconds = 0;
    const MAX_POLL_TIME = 30; // Maximum 30 seconds of polling

    // Handle Mock/Demo mode
    if (jobId === 'mock-job-id') {
      logger.log('✨ [ExtractedResultsView] Mock mode detected - skipping backend verification');
      setPollingStage('Demo data ready!');
      setIsDataReady(true);
      setIsPolling(false);
      return;
    }

    const verifyDashboardData = async (): Promise<boolean> => {
      try {
        logger.log('📊 [Dashboard Verification] Checking if data is queryable...');

        // Single verification: check both endpoints
        const [summaryResponse, categoryResponse] = await Promise.all([
          apiService.getUserHealthSummary(),
          apiService.getCategoryInsights()
        ]);

        const insightsCount = categoryResponse.data?.insights?.length || 0;
        const hasHealthScore = summaryResponse.data?.overall_health_score != null;

        logger.log('📊 [Dashboard Verification] Responses:', {
          summary: {
            success: summaryResponse.success,
            hasData: !!summaryResponse.data,
            hasHealthScore,
            score: summaryResponse.data?.overall_health_score
          },
          category: {
            success: categoryResponse.success,
            insightsCount
          }
        });

        // CRITICAL: Verify that we actually have meaningful data
        // - Health score must be present (not null/undefined)
        // - Category insights must have at least one item
        const hasMeaningfulData = hasHealthScore && insightsCount > 0;

        if (!hasMeaningfulData) {
          logger.log('⏳ [Dashboard Verification] Data not complete yet:', {
            hasHealthScore,
            insightsCount,
            needsHealthScore: !hasHealthScore,
            needsInsights: insightsCount === 0
          });
        }

        return hasMeaningfulData;
      } catch (error) {
        logger.error('❌ [Dashboard Verification] Error:', error);
        return false;
      }
    };

    const pollJobStatus = async () => {
      try {
        if (jobId) {
          logger.log(`🔍 [Job Poll] Checking job status: ${jobId} (elapsed: ${elapsedSeconds}s)`);
          const statusResponse = await apiService.getJobStatus(jobId);

          // Handle null response (404 - job not found)
          if (statusResponse === null) {
            logger.warn('⚠️ [Job Poll] Job not found (404) - may be processing or backend cold start');
            setPollingStage('Waiting for server...');
            return;
          }

          logger.log('🔍 [Job Poll] Status:', {
            jobId,
            status: statusResponse.data.status,
            progress: statusResponse.data.progress,
            stage: statusResponse.data.current_stage
          });

          if (statusResponse.data.status === 'completed') {
            logger.log('✅ [Job Poll] Job completed! Verifying dashboard data...');
            setPollingStage('Verifying data availability...');

            // Single verification check (not a loop)
            const isDashboardReady = await verifyDashboardData();

            if (isDashboardReady) {
              logger.log('✅ [Dashboard Verification] Data confirmed! Button enabled.');
              setPollingStage('Data ready!');
              setIsDataReady(true);
              setIsPolling(false);
              setCountdown(null);
              clearInterval(pollInterval);
              clearInterval(countdownInterval);
              return;
            } else {
              logger.log('⏳ [Dashboard Verification] Data not ready yet, will retry...');
              setPollingStage('Waiting for data...');
            }
          } else if (statusResponse.data.status === 'failed') {
            logger.error('❌ [Job Poll] Job failed! Allowing navigation anyway.');
            setIsDataReady(true);
            setIsPolling(false);
            setCountdown(null);
            clearInterval(pollInterval);
            clearInterval(countdownInterval);
          } else {
            logger.log(`⏳ [Job Poll] Job still processing (status: ${statusResponse.data.status})`);
            setPollingStage(statusResponse.data.current_stage || 'Processing...');
          }
        } else {
          // No jobId - verify data directly (happens when using preview endpoint)
          logger.log('⚠️ [Job Poll] No jobId - verifying dashboard data directly...');
          const isDashboardReady = await verifyDashboardData();

          if (isDashboardReady) {
            logger.log('✅ [Dashboard Verification] Data confirmed! Button enabled.');
            setPollingStage('Data ready!');
            setIsDataReady(true);
            setIsPolling(false);
            setCountdown(null);
            clearInterval(pollInterval);
            clearInterval(countdownInterval);
          }
        }

        // Timeout after MAX_POLL_TIME seconds
        if (elapsedSeconds >= MAX_POLL_TIME) {
          logger.warn(`⏱️ [Polling Timeout] ${MAX_POLL_TIME}s elapsed - allowing navigation anyway`);
          setIsDataReady(true);
          setIsPolling(false);
          setCountdown(null);
          clearInterval(pollInterval);
          clearInterval(countdownInterval);
        }
      } catch (error) {
        logger.error('❌ [Job Poll] Error during polling:', error);
        // On error after 15 seconds, allow navigation
        if (elapsedSeconds > 15) {
          logger.warn('⏱️ [Error Timeout] 15s of errors - allowing navigation anyway');
          setIsDataReady(true);
          setIsPolling(false);
          setCountdown(null);
          clearInterval(pollInterval);
          clearInterval(countdownInterval);
        }
      }
    };

    // Start polling job status every 2 seconds
    logger.log('⏰ [Polling Setup] Starting job status polling (2s interval)');
    pollInterval = setInterval(pollJobStatus, 2000);

    // Track elapsed time for countdown display
    countdownInterval = setInterval(() => {
      elapsedSeconds++;
      const remaining = Math.max(0, 10 - elapsedSeconds);
      setCountdown(remaining > 0 ? remaining : null);
    }, 1000);

    // Initial poll
    logger.log('🚀 [Initial Poll] Running first job status check...');
    pollJobStatus();

    return () => {
      logger.log('🛑 [Cleanup] Clearing polling intervals');
      clearInterval(pollInterval);
      clearInterval(countdownInterval);
    };
  }, [jobId, data.filename, data.metrics_count]);

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'normal':
        return 'var(--color-success)';
      case 'high':
      case 'low':
        return 'var(--color-warning)';
      case 'critical':
        return 'var(--color-error)';
      default:
        return 'var(--color-text-secondary)';
    }
  };

  const groupedMetrics: Record<string, HealthMetric[]> = {};

  // Safely access health_metrics with fallback to empty array
  const healthMetrics = data?.health_metrics || [];

  healthMetrics.forEach(metric => {
    const category = metric.test_name.split(' - ')[0] || 'General';
    if (!groupedMetrics[category]) {
      groupedMetrics[category] = [];
    }
    groupedMetrics[category].push(metric);
  });

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--color-background)',
      padding: 'var(--space-6)',
      fontFamily: 'var(--font-primary)'
    }}>
      {/* Debug Panel - Only shown when VITE_ENABLE_DEBUG=true */}
      {import.meta.env.VITE_ENABLE_DEBUG === 'true' && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          backgroundColor: '#1a1a1a',
          color: '#00ff00',
          padding: '12px',
          borderRadius: '8px',
          fontSize: '11px',
          fontFamily: 'monospace',
          zIndex: 9999,
          maxWidth: '300px',
          border: '2px solid #00ff00'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#ffff00' }}>🐛 DEBUG STATUS</div>
          <div>Job ID: {jobId || 'NONE'}</div>
          <div>Data Ready: {isDataReady ? '✅ YES' : '❌ NO'}</div>
          <div>Polling: {isPolling ? '🔄 YES' : '⏸️ NO'}</div>
          <div>Stage: {pollingStage}</div>
          <div>Countdown: {countdown !== null ? `${countdown}s` : '-'}</div>
          <div>Metrics: {data.metrics_count}</div>
        </div>
      )}

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: 'var(--space-8)'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: 'var(--color-success-light)',
            marginBottom: 'var(--space-4)'
          }}>
            <CheckCircle size={32} style={{ color: 'var(--color-success)' }} />
          </div>

          <h1 style={{
            fontSize: 'var(--text-3xl)',
            fontWeight: 'var(--font-bold)',
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--space-2)'
          }}>
            Extraction Complete!
          </h1>

          <p style={{
            fontSize: 'var(--text-lg)',
            color: 'var(--color-text-secondary)',
            marginBottom: 'var(--space-6)'
          }}>
            Successfully extracted {data.metrics_count} health metrics from {data.filename}
          </p>

          {/* Disclaimer */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-3)',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--color-warning-light)',
            border: '1px solid var(--color-warning)',
            borderRadius: 'var(--radius)',
            maxWidth: '600px',
            margin: '0 auto',
            textAlign: 'left'
          }}>
            <AlertTriangle size={20} style={{
              color: 'var(--color-warning)',
              flexShrink: 0,
              marginTop: '2px'
            }} />
            <div>
              <p style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text-primary)',
                fontWeight: 'var(--font-semibold)',
                marginBottom: 'var(--space-1)'
              }}>
                Please Review Before Saving
              </p>
              <p style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text-secondary)'
              }}>
                AI extraction may contain mistakes. Please verify the metrics below match your report before saving to your health dashboard.
              </p>
            </div>
          </div>
        </div>

        {/* Patient Info */}
        {data.patient_info && Object.keys(data.patient_info).length > 0 && (
          <div style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'var(--radius)',
            padding: 'var(--space-6)',
            marginBottom: 'var(--space-6)',
            boxShadow: 'var(--shadow-sm)'
          }}>
            <h2 style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--color-text-primary)',
              marginBottom: 'var(--space-4)'
            }}>
              Patient Information
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 'var(--space-4)'
            }}>
              {data.patient_info.name && (
                <div>
                  <p style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--space-1)'
                  }}>Name</p>
                  <p style={{
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-text-primary)',
                    fontWeight: 'var(--font-medium)'
                  }}>{data.patient_info.name}</p>
                </div>
              )}
              {data.patient_info.age && (
                <div>
                  <p style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--space-1)'
                  }}>Age</p>
                  <p style={{
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-text-primary)',
                    fontWeight: 'var(--font-medium)'
                  }}>{data.patient_info.age}</p>
                </div>
              )}
              {data.patient_info.gender && (
                <div>
                  <p style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--space-1)'
                  }}>Gender</p>
                  <p style={{
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-text-primary)',
                    fontWeight: 'var(--font-medium)'
                  }}>{data.patient_info.gender}</p>
                </div>
              )}
              {data.patient_info.report_date && (
                <div>
                  <p style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--space-1)'
                  }}>Report Date</p>
                  <p style={{
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-text-primary)',
                    fontWeight: 'var(--font-medium)'
                  }}>{data.patient_info.report_date}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Extracted Metrics */}
        <div style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: 'var(--radius)',
          padding: 'var(--space-6)',
          marginBottom: 'var(--space-6)',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <h2 style={{
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--space-4)'
          }}>
            Extracted Health Metrics
          </h2>

          {Object.entries(groupedMetrics).map(([category, metrics]) => (
            <div key={category} style={{
              marginBottom: 'var(--space-6)'
            }}>
              <h3 style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-primary)',
                marginBottom: 'var(--space-3)',
                paddingBottom: 'var(--space-2)',
                borderBottom: '2px solid var(--color-border)'
              }}>
                {category}
              </h3>

              <div style={{
                display: 'grid',
                gap: 'var(--space-3)'
              }}>
                {metrics.map((metric, index) => (
                  <div key={index} style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1.5fr 0.5fr',
                    gap: 'var(--space-4)',
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--color-background)',
                    borderRadius: 'var(--radius)',
                    alignItems: 'center'
                  }}>
                    <div>
                      <p style={{
                        fontSize: 'var(--text-base)',
                        color: 'var(--color-text-primary)',
                        fontWeight: 'var(--font-medium)'
                      }}>
                        {metric.test_name}
                      </p>
                    </div>

                    <div>
                      <p style={{
                        fontSize: 'var(--text-base)',
                        color: 'var(--color-text-primary)',
                        fontWeight: 'var(--font-semibold)'
                      }}>
                        {metric.value} {metric.unit || ''}
                      </p>
                    </div>

                    <div>
                      <p style={{
                        fontSize: 'var(--text-sm)',
                        color: 'var(--color-text-secondary)'
                      }}>
                        {metric.reference_range ? `Range: ${metric.reference_range}` : 'No range available'}
                      </p>
                    </div>

                    <div style={{
                      textAlign: 'right'
                    }}>
                      {metric.status && (
                        <span style={{
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-semibold)',
                          color: getStatusColor(metric.status),
                          padding: 'var(--space-1) var(--space-2)',
                          backgroundColor: `${getStatusColor(metric.status)}15`,
                          borderRadius: 'var(--radius)',
                          textTransform: 'uppercase'
                        }}>
                          {metric.status}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Action Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 'var(--space-8)'
        }}>
          <button
            onClick={() => {
              logger.log('🖱️ [Button Click] "Go to Dashboard" clicked', {
                isDataReady,
                isPolling,
                pollingStage
              });
              onNavigateToDashboard();
            }}
            disabled={!isDataReady}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-4) var(--space-8)',
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: isDataReady ? 'var(--color-background)' : '#ffffff',
              backgroundColor: isDataReady ? 'var(--color-primary)' : '#9CA3AF',
              border: 'none',
              borderRadius: 'var(--radius)',
              cursor: isDataReady ? 'pointer' : 'not-allowed',
              boxShadow: 'var(--shadow-md)',
              transition: 'all 0.2s ease',
              opacity: isDataReady ? 1 : 0.7
            }}
            onMouseOver={(e) => {
              if (isDataReady) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
              }
            }}
            onMouseOut={(e) => {
              if (isDataReady) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'var(--shadow-md)';
              }
            }}
          >
            {isPolling ? (
              <>
                <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                {countdown !== null && countdown > 0 ? (
                  `${pollingStage} (${countdown}s)`
                ) : (
                  pollingStage
                )}
              </>
            ) : (
              <>
                {jobId === 'mock-job-id' ? 'Sign up to track trends' : 'Go to Health Dashboard'}
                <ArrowRight size={20} />
              </>
            )}
          </button>
        </div>

        {/* Processing Note */}
        <div style={{
          textAlign: 'center',
          marginTop: 'var(--space-6)'
        }}>
          <p style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--color-text-secondary)',
            fontStyle: 'italic'
          }}>
            {isPolling
              ? 'Your metrics are being saved and will appear on your dashboard shortly...'
              : 'Your metrics have been saved successfully!'
            }
          </p>
        </div>
      </div>
    </div>
  );
};
